import { memo } from 'react';
import { Badge, Divider, Group, Modal, Stack, Text } from '@mantine/core';
import {
  CourseQuizAnswers,
  type MappedQuizAnswer,
  type QuizAttempt,
} from './CourseQuizAnswers';

export type CourseDetail = {
  courseId: number;
  courseName: string;
  current_time: number;
  current_section: number;
  completed_at?: string | null;
  passed_at?: string | null;
  has_review?: boolean;
  review?: {
    relevance?: string | number | null;
    quality?: string | number | null;
    utility?: string | number | null;
    time?: string | number | null;
    sponsor?: string | number | null;
    notes?: string | null;
  } | string | null;
  progress_percentage?: number;
  total_chapters?: number;
  quiz_attempts?: QuizAttempt[] | null;
  mappedQuestionsAnswers?: MappedQuizAnswer[] | null;
  answer_1?: unknown;
  answer_2?: unknown;
  answer_3?: unknown;
  answer_4?: unknown;
  answer_5?: unknown;
};

type CourseDetailModalProps = {
  opened: boolean;
  onClose: () => void;
  course: CourseDetail | undefined;
};

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('it-IT');
}

export const CourseDetailModal = memo<CourseDetailModalProps>(
  ({ opened, onClose, course }) => {
    if (!course) {
      return (
        <Modal opened={opened} onClose={onClose} title='Dettaglio corso'>
          <Text c='dimmed' size='sm'>
            Nessun corso selezionato.
          </Text>
        </Modal>
      );
    }

    const progress =
      course.progress_percentage != null
        ? `${Number(course.progress_percentage).toFixed(0)}%`
        : null;

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={course.courseName}
        size='lg'
      >
        <Stack gap='md'>
          <Group gap='xs'>
            <Badge variant='light' color='blue'>
              Section{' '}
              {Number.isFinite(Number(course.current_section)) &&
              Number(course.current_section) >= 0
                ? Number(course.current_section) + 1
                : '—'}
            </Badge>
            <Badge variant='light' color='gray'>
              {Math.floor((course.current_time || 0) / 60)}m{' '}
              {(course.current_time || 0) % 60}s
            </Badge>
            {progress ? (
              <Badge variant='light' color='violet'>
                Progress {progress}
              </Badge>
            ) : null}
            <Badge
              variant='light'
              color={course.completed_at ? 'green' : 'red'}
            >
              Completed {formatDate(course.completed_at)}
            </Badge>
            <Badge variant='light' color={course.passed_at ? 'green' : 'red'}>
              Passed {formatDate(course.passed_at)}
            </Badge>
            <Badge variant='light' color={course.has_review ? 'green' : 'gray'}>
              {course.has_review ? 'Reviewed' : 'No review'}
            </Badge>
          </Group>

          <Divider />

          <CourseQuizAnswers
            quizAttempts={course.quiz_attempts}
            mappedQuestionsAnswers={course.mappedQuestionsAnswers}
            answer_1={course.answer_1}
            answer_2={course.answer_2}
            answer_3={course.answer_3}
            answer_4={course.answer_4}
            answer_5={course.answer_5}
          />
        </Stack>
      </Modal>
    );
  }
);

CourseDetailModal.displayName = 'CourseDetailModal';
